// iot-admin/client/src/pages/DriverDetail.js
//
// One driver — the PERSON — and every cab they have driven.
//
// Unlike media.pdooh's equivalent this history is NOT tenant-clipped: iot-admin
// is the cross-tenant control plane, and an admin reconciling a driver needs
// every cab regardless of which operator owns it. The tenant is shown per row
// precisely because it may differ.
//
// ── EDITING LIVES HERE, NOT ON THE DEVICE PAGE ──────────────────────────────
// The device page can now only ASSIGN (link a person to a cab). Editing who a
// person IS belongs on the person, where the consequence can be stated plainly:
// a rename here changes them on every cab and throughout their history. That
// separation is the whole point of the V249 refactor — before it, one form did
// both and the destructive reading was the default.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { driverService } from '../services/api';
import '../components/DeviceSimTaxiDriver.css'; // std-input / std-muted / std-hint / std-form-actions

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

/** Duration of an assignment, or how long it has been running. */
const span = (from, to) => {
  if (!from) return '';
  const days = Math.max(0, Math.round(((to ? new Date(to) : new Date()) - new Date(from)) / 86400000));
  if (days === 0) return 'less than a day';
  if (days === 1) return '1 day';
  if (days < 60) return `${days} days`;
  const m = Math.round(days / 30);
  return m < 24 ? `${m} months` : `${Math.round(m / 12)} years`;
};

const DriverDetail = () => {
  const { driverId } = useParams();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', mobile: '' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchDriver = useCallback(async () => {
    try {
      setLoading(true);
      const res = await driverService.getById(driverId);
      setDriver(res.data);
      setForm({ name: res.data.name || '', mobile: res.data.mobile || '' });
      setError(null);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Driver not found' : 'Failed to load driver');
    } finally {
      setLoading(false);
    }
  }, [driverId]);

  useEffect(() => { fetchDriver(); }, [fetchDriver]);

  const save = async () => {
    if (form.name.trim().length < 2) { setError('Name must be at least 2 characters'); return; }
    setSaving(true);
    setError(null);
    try {
      // Person-scoped, so this works for a driver with no cab at all — and the
      // server mirrors the new name onto every device_drivers row this person
      // holds, so no cab is left showing a stale snapshot.
      await driverService.update(driverId, {
        name: form.name.trim(),
        mobile: form.mobile.trim() || null,
      });
      setMessage('Driver updated');
      setEditing(false);
      await fetchDriver();
    } catch (e) {
      // 409 = that mobile already belongs to someone else. The server refuses
      // rather than merging two humans.
      setError(e.response?.data?.error || 'Failed to update driver');
    } finally {
      setSaving(false);
    }
  };

  // Only ever available for a driver with no assignments at all. The server
  // enforces this independently; the UI hides the button so the refusal is not
  // the way an admin discovers the rule.
  const remove = async () => {
    if (!window.confirm(`Delete ${driver.name}? This driver has never been assigned to a cab, so nothing is lost.`)) return;
    setDeleting(true);
    setError(null);
    try {
      await driverService.remove(driverId);
      navigate('/drivers');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to delete driver');
      setDeleting(false);
    }
  };

  if (loading) return <div className="loading"><div className="spinner"></div></div>;
  if (!driver) {
    return (
      <div className="users-page">
        <Link to="/drivers" className="back-link">← Back to Drivers</Link>
        <div className="alert alert-error">❌ {error || 'Driver not found'}</div>
      </div>
    );
  }

  const active = driver.assignments.filter((a) => a.isActive);
  const past = driver.assignments.filter((a) => !a.isActive);

  return (
    <div className="users-page">
      <Link to="/drivers" className="back-link">← Back to Drivers</Link>

      {message && <div className="alert alert-success">✅ {message}</div>}
      {error && <div className="alert alert-error">❌ {error}</div>}

      <div className="user-profile-card">
        <div className="profile-header">
          <div className="user-avatar large">🧑‍✈️</div>
          <div className="profile-info">
            <h1>{driver.name}</h1>
            <p className="email">{driver.mobile || 'No mobile on file'}</p>
          </div>
        </div>
      </div>

      {/* ── Person ── */}
      <div className="detail-section">
        <div className="section-header">
          <h2>Person</h2>
          {!editing && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>✏️ Edit details</button>
              {driver.assignments.length === 0 && (
                <button className="btn btn-danger btn-sm" onClick={remove} disabled={deleting}>
                  {deleting ? 'Deleting…' : '🗑️ Delete'}
                </button>
              )}
            </div>
          )}
        </div>

        {editing ? (
          <>
            {/* State the blast radius before the click, not after. */}
            {driver.assignments.length > 0 ? (
              <div className="alert alert-warning">
                ⚠️ This edits <strong>the person</strong>. {driver.name} is on{' '}
                {driver.assignments.length} assignment{driver.assignments.length === 1 ? '' : 's'} —
                the change applies to all of them and to their history, and shows on the
                operator&rsquo;s console too. To put a <em>different</em> person in a cab, use
                Change driver on that device instead.
              </div>
            ) : (
              <div className="std-hint">
                This driver has never been assigned to a cab, so this edit affects nothing else.
              </div>
            )}
            <div className="detail-grid">
              <div className="detail-item">
                <label>Name</label>
                <input className="std-input" value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="detail-item">
                <label>Mobile</label>
                <input className="std-input" value={form.mobile} placeholder="+91…"
                  onChange={(e) => setForm((f) => ({ ...f, mobile: e.target.value }))} />
                <small className="std-muted">
                  The mobile identifies this person. Changing it to a number another driver
                  already has is refused rather than merging them.
                </small>
              </div>
              <div className="detail-item std-form-actions">
                <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
                  {saving ? 'Saving…' : '💾 Save person'}
                </button>
                <button className="btn btn-secondary btn-sm" disabled={saving}
                  onClick={() => { setEditing(false); setForm({ name: driver.name || '', mobile: driver.mobile || '' }); }}>
                  Cancel
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="detail-grid">
            <div className="detail-item"><label>Name</label><span>{driver.name}</span></div>
            <div className="detail-item"><label>Mobile</label><span>{driver.mobile || '—'}</span></div>
            <div className="detail-item">
              <label>Identity documents</label>
              <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {[['Aadhaar', driver.kyc?.aadhaar], ['PAN', driver.kyc?.pan], ['Licence', driver.kyc?.license]].map(
                  ([label, present]) => (
                    <span key={label}
                      className={`status-badge ${present ? 'status-active' : 'status-pending'}`}
                      title={present ? `${label} on file` : `${label} missing`}
                      style={{ fontSize: '0.7rem' }}>
                      {present ? '✓' : '○'} {label}
                    </span>
                  ),
                )}
              </span>
              <small className="std-muted">
                Documents are uploaded against the cab on that device&rsquo;s page.
              </small>
            </div>
            <div className="detail-item"><label>Known since</label><span>{fmtDate(driver.createdAt)}</span></div>
            <div className="detail-item"><label>Driver ID</label><span className="mono">{driver.id}</span></div>
          </div>
        )}
      </div>

      {/* ── Currently driving ── */}
      <div className="detail-section">
        <h2>🚕 Currently driving</h2>
        {active.length === 0 ? (
          <div className="empty-state-small">
            <p>Not assigned to any cab.</p>
            <p className="muted">Assign a driver from that cab&rsquo;s device page.</p>
          </div>
        ) : (
          <div className="tenant-list">
            {active.map((a) => (
              <div key={a.id} className="tenant-assignment-item">
                <div className="tenant-info">
                  <Link to={`/devices/${a.deviceId}`} className="tenant-name">🚕 {a.deviceName}</Link>
                  {a.city && <span className="tenant-plan" style={{ marginLeft: '0.5rem' }}>{a.city}</span>}
                </div>
                <div className="tenant-role">
                  <span className="std-muted">{a.tenantName || 'Unassigned tenant'}</span>
                </div>
                <span className="std-muted">since {fmtDate(a.assignedAt)} · {span(a.assignedAt, null)}</span>
              </div>
            ))}
          </div>
        )}
        {active.length > 1 && (
          <p className="std-hint">
            🔗 This driver holds {active.length} cabs at once. Legal, but worth confirming it is
            intended rather than a missed unassign.
          </p>
        )}
      </div>

      {/* ── History ── */}
      {past.length > 0 && (
        <div className="detail-section">
          <h2>Previous cabs</h2>
          <table className="std-history">
            <thead>
              <tr><th>Cab</th><th>Operator</th><th>From</th><th>To</th><th>Duration</th></tr>
            </thead>
            <tbody>
              {past.map((a) => (
                <tr key={a.id}>
                  <td><Link to={`/devices/${a.deviceId}`}>{a.deviceName}</Link></td>
                  <td>{a.tenantName || '—'}</td>
                  <td>{fmtDate(a.assignedAt)}</td>
                  <td>{fmtDate(a.unassignedAt)}</td>
                  <td>{span(a.assignedAt, a.unassignedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DriverDetail;
